import React, { CSSProperties, useCallback, useEffect, useMemo } from 'react';

import { Dictionary, noop as _noop, debounce } from 'lodash';
import type {
	Driver,
	ServiceArea,
	User,
	Location,
	Team,
	Vehicle,
	VehicleType,
	OPTIMIZATION_TYPE,
	DeliveryBlock,
	ParkingSpot,
	CustomerAvailabilityHour,
	CustomAttributeRes
} from '@bringg/types';
import type { Directions } from '@bringg/types/types/directions';
import type { EtaFetchFunc } from '@bringg-frontend/eta-calculator';
import { BringgFontIcons } from '@bringg/bringg-icons';
import { TFunction } from 'react-i18next';

import { getTranslations } from './translations';
import RoutesPlanner, {
	DEFAULT_CONFIGURATION,
	JSXIconWithToolTip,
	RoutesPlannerBreak
} from './components/routes-planner/routes-planner';
import RoutesPlannerStore from './stores/routes-planner-store';
import type { RoutesPlannerConfiguration } from './components/routes-planner/routes-planner';
import type { DataChangesNotification, RoutesPlannerTranslations } from './components';
import { StoreProvider } from './stores/helpers/store-context';
import type { RoutesPlannerRun, RoutesPlannerDeps, RoutesPlannerTask } from './types';
import type { ConsecutiveRoutes } from './types/routes-planner-run';
import IconWithTooltip from './components/icon-with-tooltip/icon-with-tooltip';
import { OpenUserAssignerProps, RealtimeData, UpdateVehicleOnRunProps } from './types/routes-planner-app';

import './routes-planner-app.scss';

export interface RoutesPlannerProps {
	team?: Partial<Team>;
	tasks: RoutesPlannerTask[];
	drivers: Dictionary<Driver>;
	routes: Dictionary<RoutesPlannerRun>;
	consecutiveRoutes: ConsecutiveRoutes;
	deliveryBlocks: DeliveryBlock[];
	directions?: Directions[];
	breaks?: Dictionary<RoutesPlannerBreak[]>;
	mapApiKey: string;
	home?: Location | null;
	style?: CSSProperties;
	className?: string;
	mapZoom?: number;
	tFunction?: TFunction;
	onDataChanged?: (changes: DataChangesNotification) => void;
	optimizationType?: OPTIMIZATION_TYPE;
	currentUser?: User;
	onItemMoveRecalculateEtaFetch?: EtaFetchFunc;
	openOrderPage?: (taskId: number) => void;
	getAllServiceAreas: () => Promise<ServiceArea[]>;
	configuration?: RoutesPlannerConfiguration;
	optimizationRequestId: string;
	fromDispatch?: boolean;
	vehicleTypeByVehicleIds?: Dictionary<VehicleType>;
	realtimeData?: RealtimeData;
	onContactSupport: () => void;
	deps: RoutesPlannerDeps;
	vehicles: Vehicle[];
	openUserAssignerModal?: (props: OpenUserAssignerProps) => void;
	openVehicleAssignerModal?: (
		runId: number,
		onClose: ({ runId, vehicleId, tasksIds }: UpdateVehicleOnRunProps) => void
	) => void;
	customerIdByTaskInventory: { [key: string]: number };
	parkingSpots?: ParkingSpot[];
	mapIcons?: { driver: JSXIconWithToolTip; parking: JSXIconWithToolTip; home: JSXIconWithToolTip };
	isReadOnly?: boolean;
	customerAvailabilityHours: { [customerId: string]: CustomerAvailabilityHour[] };
	isOptimizationKpiEnabled?: boolean;
	customAttributes: CustomAttributeRes[];
}

const REALTIME_REFRESH_DEBOUNCE = 1000;

const RoutesPlannerApp = (props: RoutesPlannerProps) => {
	const {
		team,
		tasks,
		drivers,
		routes,
		breaks,
		deliveryBlocks,
		directions,
		home,
		tFunction,
		onDataChanged = _noop,
		onItemMoveRecalculateEtaFetch,
		optimizationType,
		currentUser,
		openOrderPage,
		getAllServiceAreas,
		configuration = DEFAULT_CONFIGURATION,
		optimizationRequestId,
		fromDispatch = false,
		realtimeData,
		vehicleTypeByVehicleIds,
		onContactSupport,
		deps,
		vehicles,
		openUserAssignerModal,
		openVehicleAssignerModal,
		customerIdByTaskInventory,
		consecutiveRoutes,
		parkingSpots,
		isReadOnly,
		customerAvailabilityHours,
		isOptimizationKpiEnabled,
		customAttributes,
		...otherProps
	} = props;

	const translations: RoutesPlannerTranslations = useMemo(() => {
		return getTranslations(tFunction);
	}, [tFunction]);

	const mapIcons = {
		driver: (color: string, title: string, id: string) => (
			<IconWithTooltip
				iconClassName="map-icon"
				id={id}
				iconName={BringgFontIcons.DriverHome}
				color={color}
				title={title}
			/>
		),
		parking: (color: string, title: string, id: string) => (
			<IconWithTooltip
				iconClassName="map-icon"
				id={id}
				iconName={BringgFontIcons.TeamParking}
				color={color}
				title={title}
			/>
		),
		home: (color = 'black') => (
			<IconWithTooltip
				iconClassName="map-icon"
				iconName={BringgFontIcons.TeamHome}
				color={color}
				title={translations.tooltipTranslations.teamsHome}
			/>
		)
	};

	const routesPlannerStore = useMemo(() => {
		return new RoutesPlannerStore({
			tasks,
			drivers,
			routes,
			deliveryBlocks,
			directions,
			breaks,
			configuration,
			home,
			translations,
			onItemMoveRecalculateEtaFetch,
			team,
			currentUser,
			optimizationType,
			onDataChanged,
			optimizationRequestId,
			fromDispatch,
			vehicleTypeByVehicleIds,
			openOrderPage,
			onContactSupport,
			vehicles,
			customerIdByTaskInventory,
			consecutiveRoutes,
			parkingSpots,
			mapIcons,
			isReadOnly,
			customerAvailabilityHours,
			isOptimizationKpiEnabled,
			openUserAssignerModal,
			openVehicleAssignerModal,
			deps,
			customAttributes
		});
	}, []);

	const debouncedRefresh = useCallback(
		debounce((realtimeData: RealtimeData) => {
			routesPlannerStore.refresh(realtimeData.tasksById, realtimeData.runIdsToUpdate);
		}, REALTIME_REFRESH_DEBOUNCE),
		[]
	);

	useEffect(() => {
		if (realtimeData) {
			debouncedRefresh(realtimeData);
		}
	}, [realtimeData]);

	return (
		<StoreProvider value={routesPlannerStore}>
			<RoutesPlanner
				className={otherProps.className}
				style={otherProps.style}
				mapApiKey={otherProps.mapApiKey}
				mapZoom={otherProps.mapZoom}
				configuration={configuration}
				getAllServiceAreas={getAllServiceAreas}
				deps={deps}
			/>
		</StoreProvider>
	);
};

export default React.memo(RoutesPlannerApp);
